import type { Category, Special, Work } from "@/types/content";
import { works } from "@/mocks/works";
import { categories } from "@/mocks/categories";
import { regions, years, sortOptions } from "@/mocks/categories";
import { specials } from "@/mocks/specials";
import { getBanner, getCover } from "@/assets/covers";

export { regions, years, sortOptions };

export const allWorks: Work[] = works;
export const allCategories: Category[] = categories;
export const allSpecials: Special[] = specials as Special[];

export const getWork = (id?: string): Work | undefined => allWorks.find((item) => item.id === id);

export const getCategory = (key?: string): Category | undefined =>
  allCategories.find((item) => item.key === key);

export const getSpecial = (id?: string): Special | undefined =>
  allSpecials.find((item) => item.id === id);

export const coverOf = (work: Work): string => getCover(work.cover);
export const bannerOf = (key: string): string => getBanner(key);

export const categoryName = (key: string): string => getCategory(key)?.name ?? "其他";

export const formatViews = (value: number): string =>
  value >= 10000 ? `${(value / 10000).toFixed(1)} 万` : `${value}`;

export const categoryCount = (key: string): number =>
  allWorks.filter((item) => item.category === key).length;

export type SortKey = "latest" | "hot" | "rating";

export const sortWorks = (list: Work[], sort: SortKey): Work[] => {
  const next = [...list];
  if (sort === "hot") {
    return next.sort((a, b) => b.views - a.views);
  }
  if (sort === "rating") {
    return next.sort((a, b) => b.rating - a.rating);
  }
  return next.sort((a, b) => b.year - a.year || b.views - a.views);
};

export const latestWorks = (limit = 20): Work[] => sortWorks(allWorks, "latest").slice(0, limit);

export const hotWorks = (limit = 8): Work[] => sortWorks(allWorks, "hot").slice(0, limit);

export const searchWorks = (keyword: string, limit = 6): Work[] => {
  const text = keyword.trim().toLowerCase();
  if (!text) return [];
  return allWorks
    .filter((item) => {
      const haystack = `${item.title} ${categoryName(item.category)} ${item.region} ${item.tags.join(" ")} ${item.director}`.toLowerCase();
      return haystack.includes(text);
    })
    .slice(0, limit);
};

export const worksByCategory = (type: string): Work[] =>
  type === "all" ? allWorks : allWorks.filter((item) => item.category === type);

export const relatedWorks = (work: Work, limit = 5): Work[] => {
  const sameCategory = allWorks.filter((item) => item.id !== work.id && item.category === work.category);
  const sharedTags = allWorks.filter(
    (item) =>
      item.id !== work.id &&
      item.category !== work.category &&
      item.tags.some((tag) => work.tags.includes(tag)),
  );
  return [...sameCategory, ...sharedTags].slice(0, limit);
};

export const specialsOfWork = (workId: string): Special[] =>
  allSpecials.filter((item) => item.workIds.includes(workId));

export interface Episode {
  no: number;
  title: string;
  duration: string;
  synopsis: string;
}

const episodePhrases = [
  "初见",
  "雨夜",
  "试探",
  "界限",
  "旧事",
  "裂缝",
  "告白",
  "距离",
  "回忆",
  "选择",
  "转折",
  "余温",
  "回声",
  "最后一页",
];

export const episodesOf = (work: Work): Episode[] =>
  Array.from({ length: work.episodeCount }, (_, index) => {
    const phrase = episodePhrases[index % episodePhrases.length];
    const minutes = 22 + ((index * 3) % 9);
    return {
      no: index + 1,
      title: `第 ${index + 1} 话 · ${phrase}`,
      duration: `${minutes} 分钟`,
      synopsis: `${work.title} 的第 ${index + 1} 话以「${phrase}」为题，由 ${work.director} 执导。剧情承接上一话的情感线，人物关系在误会与坦白之间出现新的转折。本页仅展示目录与文字资料，不含播放内容。`,
    };
  });